import { format } from "date-fns";
import { Link2, X, FileText, Phone, MessageSquareIcon, Pencil, Ban } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Session } from "@/pages/sessions/types/session";
import { useCreateNoShow } from "../hooks/use-create-no-show";
import { useUndoNoShow } from "../hooks/use-undo-no-show";
import { useCreateDocument } from "../hooks/use-create-document";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCancelSession } from "../hooks/use-cancel-session";
import { useUnmarkUnsuccessful } from "../hooks/use-mark-unsuccessful";
import { MarkUnsuccessfulDialog } from "./mark-unsuccessful-dialog";
import { toast } from "@/hooks/use-toast";
import { isWomensHealthBundle } from "@/helpers/womens-health-schedule";

const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";
import { HasAccess } from "@/components/common/has-access";
import { CANCELLATION_REASONS } from "@/utils/cancellation-reasons";
import {
  getCancellationBadges,
  getCancelActionedByName,
} from "../utils/cancellation-label";
import { BundleExpirationBadge } from "@/components/common/bundle-expiration-badge";
import { ExceptionBadge } from "@/features/exception-logs";
import { EditUsageIdDialog } from "./edit-usage-id-dialog";
import { useContactActions } from "@/features/contact-actions/hooks/use-contact-actions";

interface SessionCardProps {
  session: Session;
  onCardClick?: () => void;
  onUserClick?: (userId: string) => void;
}

export function SessionCard({ session, onCardClick, onUserClick }: SessionCardProps) {
  const {
    name,
    professional,
    status,
    start_time,
    end_time,
    invitee_email,
    invitee_full_name,
    invitee_phone,
    canceled_by,
    cancel_reason,
    source_id,
    usage_identifier,
    is_no_show,
    is_unsuccessful,
    unsuccessful_reason,
    user_id,
    usage_bundle,
  } = session;

  // Get user_id from session or usage_bundle
  const userId = user_id || (usage_bundle as any)?.users?.[0]?.id || null;
  const cancelActionedBy = getCancelActionedByName(session);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState<string>("");
  const [cancelReason, setCancelReason] = useState<string | undefined>(
    undefined
  );
  const [editUsageIdDialogOpen, setEditUsageIdDialogOpen] = useState(false);
  const { mutate: cancelSession } = useCancelSession();

  const [unsuccessfulDialogOpen, setUnsuccessfulDialogOpen] = useState(false);
  const { mutate: unmarkUnsuccessful } = useUnmarkUnsuccessful();

  const isOthersSelected =
    selectedCancelReason === "Others (type reason manually if not listed)";
  const { mutate: createDocument } = useCreateDocument();
  const { mutate: createNoShow } = useCreateNoShow();
  const { mutate: undoNoShow } = useUndoNoShow();
  const { callViaExotel, openWatiChat, calling, hasExotelPhone } = useContactActions();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: `${label} copied to clipboard` });
    } catch {
          toast({
        title: "Error",
        description: `Failed to copy ${label}`,
            variant: "destructive",
          });
    }
  };

  const copyRescheduleLink = async () => {
    if (session.source_id) {
      try {
        const lowerName = (session.name || "").toLowerCase();
        let sessionType: "individual" | "joint" | undefined;

        if (lowerName.includes("joint")) {
          sessionType = "joint";
        } else if (
          lowerName.includes("individual") ||
          lowerName.includes("partner")
        ) {
          sessionType = "individual";
        } else if (lowerName.includes("couple")) {
          sessionType = "joint";
        }

        const sessionTypeQuery = sessionType ? `?session_type=${sessionType}` : "";
        const isWomens = isWomensHealthBundle(usage_bundle?.bundle?.standard_id ?? "");
        const baseUrl = isWomens ? DASHBOARD_BASE_URL : "https://rockethealth.app";
        const rescheduleLink = `${baseUrl}/reschedule/${session.source_id}${sessionTypeQuery}`;
        copyToClipboard(rescheduleLink, "Reschedule Link");
      } catch {
        toast({
          title: "Error",
          description: "Failed to copy reschedule link",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Error",
        description: "Session source_id not available",
        variant: "destructive",
      });
    }
  };


  const getStatusBadge = () => {
    if (status === "active") {
      return (
        <Badge className="bg-green-500 text-white border-0">
          Active
        </Badge>
    );
    } else if (status === "canceled") {
      return (
        <>
          {getCancellationBadges(session).map((badge) => (
            <Badge key={badge.label} className={badge.className}>
              {badge.label}
            </Badge>
          ))}
        </>
      );
    } else {
      // For completed or other statuses
      return (
        <Badge className="bg-gray-500 text-white border-0">
          Completed
        </Badge>
      );
    }
  };

  const formatSessionDate = (date: string) => {
    return format(new Date(date), "EEEE, MMMM d, yyyy");
  };

  const formatSessionTime = (start: string, end: string) => {
    return `${format(new Date(start), "h:mm a")} - ${format(new Date(end), "h:mm a")}`;
  };

  return (
    <>
      <Card className={`w-full mb-4 border border-gray-200 shadow-sm ${onCardClick ? "hover:shadow-md transition-shadow" : ""}`}>
        {/* Cancellation Banner */}
        {status === "canceled" && (canceled_by || cancelActionedBy) && (
          <div className="bg-red-600 text-white px-4 py-2 text-sm font-medium">
            {canceled_by ? `Canceled by ${canceled_by}` : "Canceled"}
            {cancel_reason && ` (${cancel_reason})`}
            {cancelActionedBy && ` — Actioned by ${cancelActionedBy}`}
          </div>
        )}

        {/* Unsuccessful Banner */}
        {is_unsuccessful && (
          <div className="bg-amber-600 text-white px-4 py-2 text-sm font-medium">
            Marked unsuccessful — not counted against bundle
            {unsuccessful_reason && ` (${unsuccessful_reason})`}
          </div>
        )}

        <CardContent 
          className={`p-6 ${onCardClick ? "cursor-pointer" : ""}`}
          onClick={(e) => {
            // Only trigger card click if clicking on the card itself, not on buttons or interactive elements
            if (onCardClick && (e.target as HTMLElement).closest('button') === null && (e.target as HTMLElement).closest('a') === null) {
              onCardClick();
            }
          }}
        >
          {/* Title and Status */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">
                {name}
              </h3>
              {getStatusBadge()}
              {is_no_show && (
                <Badge className="bg-gray-500 text-white border-0">
                  No Show
                </Badge>
              )}
              {is_unsuccessful && (
                <Badge className="bg-amber-600 text-white border-0">
                  Unsuccessful
                </Badge>
              )}
              {/* Bundle Expiration Badge */}
              {usage_bundle && (
                <BundleExpirationBadge
                  endDate={usage_bundle.end_date}
                  utilizedSessions={usage_bundle.utilized_session_quantity}
                  totalSessions={usage_bundle.total_session_quantity}
                />
              )}
              {/* Exception visibility badge */}
              <ExceptionBadge exceptionLogs={session.exceptionLogs} side="bottom" />
        </div>
          </div>

          {/* Usage ID */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-gray-600">Usage ID: </span>
            <span className="text-sm font-mono text-gray-900">
              {usage_identifier || ""}
            </span>
            <HasAccess permission="write">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditUsageIdDialogOpen(true);
                }}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </HasAccess>
          </div>

          {/* Quick Actions. Copy stays for anyone who can see the card; the rest
              change the session or reach the patient, so they need Edit. */}
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-700 mb-2">
                QUICK ACTIONS
        </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={copyRescheduleLink}
                  disabled={!session.source_id}
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Copy Reschedule Link
                </Button>
                <HasAccess permission="write">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={status === "canceled"}
                >
                  <X className="h-3 w-3 mr-1" />
                  Cancel Session
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-gray-600 border-gray-600 hover:bg-gray-50"
                  onClick={() => callViaExotel(invitee_phone || "")}
                  disabled={!invitee_phone || status === "canceled" || calling || !hasExotelPhone}
                  title={!hasExotelPhone ? "Set your ExoTel number in Admin Panel → Internal Users" : undefined}
                >
                  <Phone className="h-3 w-3 mr-1" />
                  {calling ? "Calling…" : "Call"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-gray-600 border-gray-600 hover:bg-gray-50"
                  onClick={() => openWatiChat(invitee_phone || "")}
                  disabled={!invitee_phone || status === "canceled"}
                >
                  <MessageSquareIcon className="h-3 w-3 mr-1" />
                  Whatsapp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-orange-600 border-orange-600 hover:bg-orange-50"
                    onClick={() => {
                      toast({
                        title: "Creating/updating document...",
                        description:
                          "Please wait...this may take a few seconds.",
                      });
                      createDocument(session.id);
                    }}
                  disabled={status === "canceled"}
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Create Doc
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-gray-600 border-gray-600 hover:bg-gray-50"
                      onClick={() => {
                        if (is_no_show) {
                          undoNoShow({ source_id: session.source_id });
                        } else {
                          createNoShow({ source_id: session.source_id });
                        }
                      }}
                  disabled={status === "canceled"}
                    >
                  <X className="h-3 w-3 mr-1" />
                      {is_no_show ? "Undo Mark No Show" : "Mark No Show"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs bg-white text-amber-700 border-amber-600 hover:bg-amber-50"
                  onClick={() => {
                    if (is_unsuccessful) {
                      unmarkUnsuccessful({ source_id: session.source_id });
                    } else {
                      setUnsuccessfulDialogOpen(true);
                    }
                  }}
                >
                  <Ban className="h-3 w-3 mr-1" />
                  {is_unsuccessful
                    ? "Unmark Unsuccessful"
                    : "Mark as Unsuccessful"}
                </Button>
                </HasAccess>
              </div>
            </div>
            {/* Divider line below Quick Actions */}
            <div className="border-t border-gray-200 mt-4 mb-4"></div>

          {/* Assigned Professional */}
          {professional && (
            <div className="mb-4">
              <span className="text-sm text-purple-600 font-medium">
                {professional.name}
              </span>
            </div>
                  )}

          {/* Date & Time */}
          <div className="mb-4">
            <div className="text-sm text-gray-700">
              {formatSessionDate(start_time)}
            </div>
            <div className="text-sm text-gray-700">
              {formatSessionTime(start_time, end_time)}
            </div>
          </div>

          {/* User Details Block */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-medium text-gray-700">User: </span>
                {invitee_full_name && onUserClick ? (
                  <span 
                    className={`font-medium cursor-pointer underline ${
                      userId 
                        ? "text-blue-600 hover:text-blue-800" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (userId) {
                        onUserClick(userId);
                      }
                    }}
                  >
                    {invitee_full_name}
                  </span>
                ) : (
                  <span className="text-gray-900">{invitee_full_name || "—"}</span>
                )}
              </div>
              <div className="text-sm">
                <span className="font-medium text-gray-700">Phone Number: </span>
                <span className="text-gray-900">{invitee_phone || "—"}</span>
              </div>
              <div className="text-sm">
                <span className="font-medium text-gray-700">Email: </span>
                <span className="text-gray-900">{invitee_email || "—"}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Dialog */}
            <Dialog
              open={cancelDialogOpen}
              onOpenChange={(open) => {
                setCancelDialogOpen(open);
                if (!open) {
                  setSelectedCancelReason("");
                  setCancelReason(undefined);
                }
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancel {invitee_full_name} Session</DialogTitle>
                </DialogHeader>
                <div className="py-4 space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Select cancellation reason
                    </label>
                    <Select
                      value={selectedCancelReason}
                      onValueChange={setSelectedCancelReason}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANCELLATION_REASONS.map((reason) => (
                          <SelectItem key={reason} value={reason}>
                            {reason}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isOthersSelected && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Please provide a reason for cancellation
                      </label>
                      <Textarea
                        placeholder="Type your reason here"
                        value={cancelReason ?? ""}
                        onChange={(e) =>
                          setCancelReason(e.target.value || undefined)
                        }
                        className="min-h-[100px]"
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCancelDialogOpen(false);
                      setSelectedCancelReason("");
                      setCancelReason(undefined);
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      const finalReason = isOthersSelected
                        ? cancelReason
                        : selectedCancelReason;

                      cancelSession({
                        source_id: session.source_id,
                        reason: finalReason,
                      });
                      setSelectedCancelReason("");
                      setCancelReason(undefined);
                      setCancelDialogOpen(false);
                    }}
                    disabled={
                      !selectedCancelReason ||
                      (isOthersSelected && !cancelReason)
                    }
                  >
                    Confirm Cancellation
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

      <MarkUnsuccessfulDialog
        source_id={session.source_id}
        open={unsuccessfulDialogOpen}
        onOpenChange={setUnsuccessfulDialogOpen}
      />

      <EditUsageIdDialog
        open={editUsageIdDialogOpen}
        onOpenChange={setEditUsageIdDialogOpen}
        source_id={session.source_id}
        current_usage_identifier={usage_identifier}
        current_bundle={usage_bundle ?? null}
      />
          </>
  );
}
